import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Link as MuiLink,
  IconButton,
  Divider,
  TextField,
  Button,
  Paper,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Facebook,
  Instagram,
  WhatsApp,
  EmailOutlined,
  PhoneOutlined,
  LocationOnOutlined,
  PrecisionManufacturingOutlined,
  RecyclingOutlined,
  Inventory2Outlined,
  LocalShippingOutlined,
  SendOutlined,
  CheckCircleOutline,
} from '@mui/icons-material';
import { localBusinessSchema } from '../SEO/schemas';
import { COLORS, withOpacity } from '../../theme/colors';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email && email.includes('@')) {
      setSubscribed(true);
      setEmail('');
    }
  };

  const trustFeatures = [
    {
      icon: <PrecisionManufacturingOutlined sx={{ fontSize: 32, color: COLORS.primary.main }} />,
      title: '0.1mm Layer Precision',
      desc: 'Industrial-grade FDM & SLA 3D printers with micron-accurate tolerance.',
      accent: COLORS.primary.main,
    },
    {
      icon: <RecyclingOutlined sx={{ fontSize: 32, color: COLORS.accent.lemon }} />,
      title: 'Eco & Bio Filaments',
      desc: 'Renewable bioplastics, food-safe PLA, tough PETG, and flexible TPU.',
      accent: COLORS.accent.lemon,
    },
    {
      icon: <Inventory2Outlined sx={{ fontSize: 32, color: COLORS.accent.coral }} />,
      title: 'Drop-Tested Packaging',
      desc: 'Cushion-packed with custom test print swatches and secure wrapping.',
      accent: COLORS.accent.coral,
    },
    {
      icon: <LocalShippingOutlined sx={{ fontSize: 32, color: COLORS.accent.sky }} />,
      title: 'Pan-India Dispatch',
      desc: 'Tracked, express doorstep shipping from our Aligarh print laboratory.',
      accent: COLORS.accent.sky,
    },
  ];

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      </Helmet>

      {/* Pre-Footer Maker Trust Strip */}
      <Box
        sx={{
          bgcolor: COLORS.surface.containerLow,
          borderTop: `1px solid ${COLORS.outline?.variant || 'rgba(0, 0, 0, 0.08)'}`,
          borderBottom: `1px solid ${COLORS.outline?.variant || 'rgba(0, 0, 0, 0.08)'}`,
          py: 5,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {trustFeatures.map((item, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    height: '100%',
                    borderRadius: '20px',
                    bgcolor: COLORS.surface.containerLowest,
                    border: `1px solid ${COLORS.outline?.variant || 'rgba(0, 0, 0, 0.08)'}`,
                    transition: 'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',
                      borderColor: item.accent,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: '16px',
                      bgcolor: withOpacity(item.accent, 0.12),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 800,
                      color: COLORS.text.primary,
                      fontFamily: '"Fredoka", "Inter", sans-serif',
                      lineHeight: 1.2,
                      mb: 0.75,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: COLORS.text.secondary, fontSize: '0.84rem', lineHeight: 1.5 }}>
                    {item.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Funky multicolor accent strip */}
      <Box
        sx={{
          height: 3,
          width: '100%',
          background: `linear-gradient(90deg, ${COLORS.primary.main} 0%, ${COLORS.accent.lemon} 28%, ${COLORS.accent.coral} 52%, ${COLORS.accent.sky} 76%, ${COLORS.accent.grape} 100%)`,
        }}
      />

      {/* Main Footer Body */}
      <Box
        component="footer"
        sx={{
          bgcolor: '#191924',
          color: '#ffffff',
          pt: 7,
          pb: 4,
          mt: 'auto',
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} sx={{ mb: 5 }}>
            {/* Column 1: Brand & Maker Identity */}
            <Grid item xs={12} sm={6} md={3}>
              <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '14px',
                    bgcolor: withOpacity(COLORS.primary.main, 0.15),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 0.5,
                  }}
                >
                  <img
                    src="/images/The-Random-Store---Logo---Transparent - Copy.png"
                    alt="The Random Store"
                    style={{ height: 32, width: 32, objectFit: 'contain' }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: '"Fredoka", "Inter", sans-serif',
                      fontWeight: 800,
                      color: '#ffffff',
                      lineHeight: 1.1,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    The Random Store
                  </Typography>
                  <Typography variant="caption" sx={{ color: COLORS.primary.light, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.68rem' }}>
                    3D Print Lab &amp; Store
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.72)', lineHeight: 1.6, mb: 2.5 }}>
                Bespoke 3D printed gadgets, rapid engineering prototypes, and funky maker gifts made to order with industrial accuracy.
              </Typography>

              {/* Social & Community Links */}
              <Box display="flex" gap={1}>
                <IconButton
                  size="small"
                  component="a"
                  href="https://instagram.com/_the_randomstore_"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram profile"
                  sx={{
                    color: '#ffffff',
                    bgcolor: 'rgba(255, 255, 255, 0.08)',
                    borderRadius: '12px',
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: COLORS.accent.coral, transform: 'translateY(-2px)' },
                  }}
                >
                  <Instagram fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  component="a"
                  href="https://www.facebook.com/profile.php?id=61574113310837"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook page"
                  sx={{
                    color: '#ffffff',
                    bgcolor: 'rgba(255, 255, 255, 0.08)',
                    borderRadius: '12px',
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: COLORS.accent.sky, transform: 'translateY(-2px)' },
                  }}
                >
                  <Facebook fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  component="a"
                  href="https://wa.me/919717083055"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp direct maker chat"
                  sx={{
                    color: '#ffffff',
                    bgcolor: 'rgba(255, 255, 255, 0.08)',
                    borderRadius: '12px',
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: COLORS.primary.main, transform: 'translateY(-2px)' },
                  }}
                >
                  <WhatsApp fontSize="small" />
                </IconButton>
              </Box>
            </Grid>

            {/* Column 2: Navigation Links */}
            <Grid item xs={6} sm={6} md={2}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#ffffff',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontSize: '0.75rem',
                  mb: 2,
                }}
              >
                Explore Lab
              </Typography>
              <Box display="flex" flexDirection="column" gap={1.2}>
                <MuiLink component={RouterLink} to="/" underline="hover" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.88rem', '&:hover': { color: COLORS.primary.light } }}>
                  Home
                </MuiLink>
                <MuiLink component={RouterLink} to="/products" underline="hover" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.88rem', '&:hover': { color: COLORS.primary.light } }}>
                  All Products
                </MuiLink>
                <MuiLink component={RouterLink} to="/services/3d-printing" underline="hover" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.88rem', '&:hover': { color: COLORS.primary.light } }}>
                  Instant 3D Quote
                </MuiLink>
                <MuiLink component={RouterLink} to="/services/3d-design-prototyping" underline="hover" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.88rem', '&:hover': { color: COLORS.primary.light } }}>
                  Design &amp; Prototyping
                </MuiLink>
                <MuiLink component={RouterLink} to="/about" underline="hover" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.88rem', '&:hover': { color: COLORS.primary.light } }}>
                  About Our Lab
                </MuiLink>
              </Box>
            </Grid>

            {/* Column 3: Customer Care & Info */}
            <Grid item xs={6} sm={6} md={3}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#ffffff',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontSize: '0.75rem',
                  mb: 2,
                }}
              >
                Maker Support
              </Typography>
              <Box display="flex" flexDirection="column" gap={1.2}>
                <MuiLink component={RouterLink} to="/faq" underline="hover" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.88rem', '&:hover': { color: COLORS.primary.light } }}>
                  FAQ &amp; Print Tolerances
                </MuiLink>
                <MuiLink component={RouterLink} to="/contact" underline="hover" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.88rem', '&:hover': { color: COLORS.primary.light } }}>
                  Contact Engineers
                </MuiLink>
                <MuiLink component={RouterLink} to="/orders" underline="hover" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.88rem', '&:hover': { color: COLORS.primary.light } }}>
                  Track Orders
                </MuiLink>
                <MuiLink component={RouterLink} to="/my-quotes" underline="hover" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.88rem', '&:hover': { color: COLORS.primary.light } }}>
                  Saved CAD Quotes
                </MuiLink>
                <MuiLink component={RouterLink} to="/terms" underline="hover" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.88rem', '&:hover': { color: COLORS.primary.light } }}>
                  Terms &amp; Disclaimers
                </MuiLink>
              </Box>
            </Grid>

            {/* Column 4: Newsletter & Lab Contact */}
            <Grid item xs={12} sm={6} md={4}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#ffffff',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontSize: '0.75rem',
                  mb: 1.5,
                }}
              >
                Join the Chaos Club
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.84rem', mb: 2, lineHeight: 1.5 }}>
                Get early alerts for limited edition prints, custom open-source STLs, and maker workbench discounts.
              </Typography>

              {/* Newsletter Form */}
              {subscribed ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    bgcolor: 'rgba(74, 191, 24, 0.15)',
                    borderRadius: '14px',
                    border: `1px solid ${COLORS.primary.main}`,
                    mb: 3,
                  }}
                >
                  <CheckCircleOutline sx={{ color: COLORS.primary.light }} />
                  <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600 }}>
                    You're on the list! Welcome to the maker family.
                  </Typography>
                </Box>
              ) : (
                <Box component="form" onSubmit={handleSubscribe} sx={{ mb: 3 }}>
                  <Box display="flex" gap={1}>
                    <TextField
                      size="small"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      variant="outlined"
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'rgba(255, 255, 255, 0.06)',
                          color: '#ffffff',
                          borderRadius: '999px',
                          fontSize: '0.86rem',
                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                          '&:hover fieldset': { borderColor: COLORS.primary.light },
                          '&.Mui-focused fieldset': { borderColor: COLORS.primary.main },
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: 'rgba(255, 255, 255, 0.45)',
                          opacity: 1,
                        },
                      }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      endIcon={<SendOutlined sx={{ fontSize: '0.95rem !important' }} />}
                      sx={{
                        borderRadius: 999,
                        px: 2.5,
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Join
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Lab Contact Snippets */}
              <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" alignItems="center" gap={1.2}>
                  <LocationOnOutlined sx={{ fontSize: 18, color: COLORS.primary.light }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem' }}>
                    324/3 Keshav Nagar, Aligarh City, UP 202001
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1.2}>
                  <PhoneOutlined sx={{ fontSize: 18, color: COLORS.primary.light }} />
                  <MuiLink href="tel:+919717083055" underline="hover" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem', '&:hover': { color: COLORS.primary.light } }}>
                    +91 97170 83055
                  </MuiLink>
                </Box>
                <Box display="flex" alignItems="center" gap={1.2}>
                  <EmailOutlined sx={{ fontSize: 18, color: COLORS.primary.light }} />
                  <MuiLink href="mailto:info@therandomstore.com" underline="hover" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem', '&:hover': { color: COLORS.primary.light } }}>
                    info@therandomstore.com
                  </MuiLink>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.08)' }} />

          {/* Bottom Copyright & Legal Links */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.82rem' }}>
              &copy; {new Date().getFullYear()} The Random Store. Engineered with passion and precision.
            </Typography>

            <Box display="flex" gap={2.5} flexWrap="wrap">
              <MuiLink component={RouterLink} to="/privacy" underline="hover" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.82rem', '&:hover': { color: '#ffffff' } }}>
                Privacy Policy
              </MuiLink>
              <MuiLink component={RouterLink} to="/terms" underline="hover" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.82rem', '&:hover': { color: '#ffffff' } }}>
                Terms of Service
              </MuiLink>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Footer;
